import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'

// Define optimized team interface for Redux store
interface CollectorItem {
  id: string
  description?: string
  options: Record<string, any>
}

interface OptimizedTeam {
  dashboardId: string
  id?: string // Original ID from API response
  name: string
  componentId: string
  buildDurationThreshold: number
  collectorItems?: {
    SCM?: CollectorItem[]
    Build?: CollectorItem[]
    Deployment?: CollectorItem[]
    AgileTool?: CollectorItem[]
    QA?: CollectorItem[]
    [key: string]: CollectorItem[] | undefined
  }
  teamInfo?: {
    teamName: string
    teamId: string
  }
  activeWidgets?: string[]
  // Include only essential metadata
  programId?: string
  productDashboardId?: string
  configurationItemBusAppName?: string
  type?: string
  scoreEnabled?: boolean
  // Feature-specific fields
  featureProjectId?: string // The projectId from the feature widget
  sprintType?: string // The sprintType from the feature widget options
  listType?: string // The listType from the feature widget options
  commitmentMet?: number | null // The commitmentMet threshold from the feature widget
  onTimeDelivery?: number | null // The onTimeDelivery threshold from the feature widget
  velocity?: number | null // The velocity threshold from the feature widget
  overallAutomationCoverage?: number | null // The overallAutomationCoverage threshold from the feature widget
  // Extracted from collectorItems.AgileTool for API calls
  agileToolTeamId?: string // The teamId from AgileTool collector item
  agileToolProjectId?: string // The projectId from AgileTool collector item
}

interface TeamState {
  teams: OptimizedTeam[]
  selectedTeamId: string | null
  isLoading: boolean
  error: string | null
  buildDetails: {
    selectedBuildId: string | null
  }
  codeRepoPanel: {
    panelType: string | null
  }
}

const initialState: TeamState = {
  teams: [],
  selectedTeamId: null,
  isLoading: false,
  error: null,
  buildDetails: {
    selectedBuildId: null,
  },
  codeRepoPanel: {
    panelType: null,
  },
}

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    setTeams: (state, action: PayloadAction<OptimizedTeam[]>) => {
      // Extract and store teamId and projectId from AgileTool collector items
      const teamsWithAgileToolIds = action.payload.map(team => {
        const agileToolItem = team.collectorItems?.AgileTool?.[0]
        if (agileToolItem?.options) {
          return {
            ...team,
            agileToolTeamId: agileToolItem.options.teamId || undefined,
            agileToolProjectId: agileToolItem.options.projectId || undefined,
          }
        }
        return team
      })

      state.teams = teamsWithAgileToolIds
    },
    setSelectedTeam: (state, action: PayloadAction<string>) => {
      state.selectedTeamId = action.payload
    },
    clearSelectedTeam: state => {
      state.selectedTeamId = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setBuildData: (state, action: PayloadAction<{buildId: string}>) => {
      state.buildDetails.selectedBuildId = action.payload.buildId
    },
    clearBuildData: state => {
      state.buildDetails.selectedBuildId = null
    },
    setCodeRepoPanelData: (state, action: PayloadAction<{panelType: string}>) => {
      state.codeRepoPanel.panelType = action.payload.panelType
    },
    clearCodeRepoPanelData: state => {
      state.codeRepoPanel.panelType = null
    },
  },
})

export const {
  setTeams,
  setSelectedTeam,
  clearSelectedTeam,
  setLoading,
  setError,
  setBuildData,
  clearBuildData,
  setCodeRepoPanelData,
  clearCodeRepoPanelData,
} = teamSlice.actions
export default teamSlice.reducer
